import cn from 'classnames'
import { memo } from 'react'

import { PartnerWorkflowStatus } from 'clickNgo/entities/partner/configs'
import { IPartner, IPartnerFlags, PartnerFlagRequestStatus } from 'clickNgo/entities/partner/models'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Tooltip } from 'core-components/library/tooltip'

import { companyDetailsTranslations } from '../../translation'

import styles from './CompanyDetailsStatus.module.scss'

type Props = {
  partner: IPartner
  partnerFlags: IPartnerFlags
}

const PARTNER_PROCESS_STATUSES = [
  PartnerWorkflowStatus.AWAITING,
  PartnerWorkflowStatus.DRAFT,
  PartnerWorkflowStatus.NEW,
  PartnerWorkflowStatus.APPROVED,
  PartnerWorkflowStatus.WIP,
]

const FLAG_PROCESS_STATUSES = [PartnerFlagRequestStatus.New, PartnerFlagRequestStatus.WIP]

export const CompanyDetailsStatus = memo<Props>(({ partner, partnerFlags }) => {
  const hasBasicRequest = PARTNER_PROCESS_STATUSES.includes(partner.workflowStatus)
  const hasLegalRequest = PARTNER_PROCESS_STATUSES.includes(partner.legalWorkflowStatus)
  const hasProgramRequests = partnerFlags.program.some(
    (flag) => flag.status && FLAG_PROCESS_STATUSES.includes(flag.status)
  )
  const hasAccreditationRequests = partnerFlags.accreditation.some(
    (flag) => flag.status && FLAG_PROCESS_STATUSES.includes(flag.status)
  )

  return (
    <RenderWhen
      condition={
        hasBasicRequest || hasLegalRequest || hasProgramRequests || hasAccreditationRequests
      }
    >
      <div className={styles.container}>
        <RenderWhen condition={hasBasicRequest}>
          <Tooltip
            className={styles.tooltip}
            tipContent={() => <Translation text={companyDetailsTranslations.statuses.basic} />}
          >
            <div className={cn(styles.status, styles.basic)} />
          </Tooltip>
        </RenderWhen>
        <RenderWhen condition={hasLegalRequest}>
          <Tooltip
            className={styles.tooltip}
            tipContent={() => <Translation text={companyDetailsTranslations.statuses.legal} />}
          >
            <div className={cn(styles.status, styles.legal)} />
          </Tooltip>
        </RenderWhen>
        <RenderWhen condition={hasProgramRequests}>
          <Tooltip
            className={styles.tooltip}
            tipContent={() => <Translation text={companyDetailsTranslations.statuses.program} />}
          >
            <div className={cn(styles.status, styles.program)} />
          </Tooltip>
        </RenderWhen>
        <RenderWhen condition={hasAccreditationRequests}>
          <Tooltip
            className={styles.tooltip}
            tipContent={() => (
              <Translation text={companyDetailsTranslations.statuses.accreditation} />
            )}
          >
            <div className={cn(styles.status, styles.accreditation)} />
          </Tooltip>
        </RenderWhen>
      </div>
    </RenderWhen>
  )
})

CompanyDetailsStatus.displayName = 'CompanyDetailsStatus'
